import React, {useState} from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import AbsoluteDropdownContainer from '../AbsoluteDropdownContainer';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from '../buttons/Button';
import Menu, {MenuProps} from './Menu';
import Option from './Option';

type OverflowButtonProps = Partial<ButtonProps> & {
  enableTooltip: boolean;
  tooltipPlacement?: FloatingPlacement;
  isIconHorizontal?: boolean;
};

export function OverflowButton(props: OverflowButtonProps): JSX.Element {
  const {enableTooltip, tooltipPlacement, isIconHorizontal = true, ...buttonProps} = props;
  return (
    <Tooltip isDisabled={!enableTooltip} text="More options" placement={tooltipPlacement}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        icon={isIconHorizontal ? 'ellipsis' : 'ellipsis-vertical'}
        size={ButtonSize.MINI}
        {...buttonProps}
      />
    </Tooltip>
  );
}

export interface OverflowMenuOption {
  icon?: BenchlingIconName;
  iconClassName?: string;
  contentClassName?: string;
  contents: JSX.Element;
  onSelect?: () => void;
  href?: string;
  openInNewTab?: boolean;
  isDisabled?: boolean;
  tooltipTitle?: string | null;
  dataTestAttrs?: DataTestAttrs;
}

export interface OverflowMenuProps {
  buttonProps?: Partial<ButtonProps>;
  menuProps?: Partial<MenuProps>;
  options: Array<OverflowMenuOption>;

  // Passed down to OverflowButton and if set to true, a tooltip will show up with the text "More options".
  // It is false by default so that callers can opt in one-by-one. (Each caller needs to carefully check the
  // rendered output - check tooltipPlacement and make sure they're not rendering another tooltip on top.)
  // Eventually, it should be true by default (opt out).
  enableTooltip?: boolean;
  tooltipPlacement?: FloatingPlacement;
  /**
   * Passed down to OverflowButton which toggles the icon to be either a vertical or horizontal ellipsis.
   * @default true.
   */
  isIconHorizontal?: boolean;
}

/**
 * A menu for hiding superfluous buttons in a table's title bar.
 */
export default function OverflowMenu({
  buttonProps,
  menuProps,
  options,
  tooltipPlacement,
  enableTooltip = false,
  isIconHorizontal = true,
}: OverflowMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <AbsoluteDropdownContainer
      menu={
        <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM} {...menuProps}>
          {options.map(({contents, ...optionProps}, index) => (
            <Option key={index} {...optionProps}>
              {contents}
            </Option>
          ))}
        </Menu>
      }
      onRequestClose={() => {
        setIsOpen(false);
      }}
      open={isOpen}
      shouldCloseOnInsideClick={true}
    >
      <OverflowButton
        className="u-mini-spacing-left"
        disabled={false}
        onClick={() => {
          setIsOpen(true);
        }}
        enableTooltip={enableTooltip}
        tooltipPlacement={tooltipPlacement}
        isIconHorizontal={isIconHorizontal}
        {...buttonProps}
      />
    </AbsoluteDropdownContainer>
  );
}
